// Candidatures (recrutement) : client axios écrit à la main, sur le modèle de
// contactMessagesClient. L'admin est un LECTEUR des données Cloudflare (D1 +
// R2 dédiés) : la liste et les documents viennent de là ; seules les
// annotations internes (notes, épingle, statut) vivent dans le SQL admin, et
// l'unique écriture Cloudflare est la suppression définitive (ligne + PDF).
// Tous les appels passent par axios pour l'intercepteur Bearer Auth0 déployé.
import axios from "axios";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export type CareerApplicationStatus = "new" | "reviewed" | "shortlisted" | "rejected";

export interface CareerDocument {
	index: number;
	name: string;
	size: number;
}

export interface CareerApplication {
	id: string;
	createdAt: string;
	sourceEnv: "preview" | "production";
	nom: string;
	prenom: string;
	email: string;
	telephone: string;
	posteId: string;
	posteTitre: string;
	domaine: string | null;
	motivation: string;
	documents: CareerDocument[];
	status: CareerApplicationStatus;
	pinned: boolean;
	notes: string;
}

export interface CareerListResult {
	items: CareerApplication[];
	total: number | null;
}

export interface CareerFilters {
	search?: string;
	posteId?: string;
	status?: CareerApplicationStatus | "";
	skip?: number;
	take?: number;
}

export interface CareerPosteOption {
	posteId: string;
	posteTitre: string;
	count: number;
}

const asArray = (data: unknown): any[] => {
	if (Array.isArray(data)) return data;
	if (data && Array.isArray((data as any).items)) return (data as any).items;
	return [];
};

const toStatus = (raw: unknown): CareerApplicationStatus =>
	raw === "reviewed" || raw === "shortlisted" || raw === "rejected" ? raw : "new";

const toDocuments = (raw: unknown): CareerDocument[] => {
	if (!Array.isArray(raw)) return [];
	return raw
		.map((d: any, i: number) => ({
			index: Number.isFinite(Number(d?.index)) ? Number(d.index) : i + 1,
			name: String(d?.name ?? `document-${i + 1}.pdf`),
			size: Number(d?.size ?? 0)
		}))
		.filter((d) => d.name.length > 0);
};

const toApplication = (raw: any): CareerApplication => ({
	id: String(raw?.id ?? ""),
	createdAt: String(raw?.createdAt ?? ""),
	sourceEnv: raw?.sourceEnv === "production" ? "production" : "preview",
	nom: String(raw?.nom ?? ""),
	prenom: String(raw?.prenom ?? ""),
	email: String(raw?.email ?? ""),
	telephone: String(raw?.telephone ?? ""),
	posteId: String(raw?.posteId ?? ""),
	posteTitre: String(raw?.posteTitre ?? ""),
	domaine: typeof raw?.domaine === "string" && raw.domaine.trim() ? raw.domaine : null,
	motivation: String(raw?.motivation ?? ""),
	documents: toDocuments(raw?.documents),
	status: toStatus(raw?.status),
	pinned: raw?.pinned === true,
	notes: String(raw?.notes ?? "")
});

export const careersClient = {
	/** Page de candidatures : filtres nom/prénom (search), poste et statut côté serveur. */
	list: async (filters: CareerFilters = {}): Promise<CareerListResult> => {
		const params: Record<string, unknown> = {
			skip: filters.skip ?? 0,
			take: filters.take ?? 25
		};
		if (filters.search) params.search = filters.search;
		if (filters.posteId) params.posteId = filters.posteId;
		if (filters.status) params.status = filters.status;
		const r = await axios.get(`${apiBase}/api/careers/applications`, { params, withCredentials: true });
		const data: any = r.data;
		const totalRaw = data && !Array.isArray(data) ? data.total : undefined;
		return {
			items: asArray(data).map(toApplication),
			total: typeof totalRaw === "number" && Number.isFinite(totalRaw) ? totalRaw : null
		};
	},

	/** Postes distincts présents dans les candidatures (options du filtre). */
	postes: async (): Promise<CareerPosteOption[]> => {
		const r = await axios.get(`${apiBase}/api/careers/applications/postes`, { withCredentials: true });
		return asArray(r.data)
			.map((p: any) => ({
				posteId: String(p?.posteId ?? ""),
				posteTitre: String(p?.posteTitre ?? ""),
				count: Number(p?.count ?? 0)
			}))
			.filter((p) => p.posteId.length > 0);
	},

	/** URL de téléchargement d'un document (streamé par l'admin depuis R2/MinIO). */
	documentUrl: (id: string, index: number): string =>
		`${apiBase}/api/careers/applications/${encodeURIComponent(id)}/documents/${index}`,

	/** Écrit l'annotation interne (notes / épingle / statut) : SQL admin, jamais Cloudflare. */
	annotate: async (
		id: string,
		annotation: { notes?: string; pinned?: boolean; status?: CareerApplicationStatus }
	): Promise<void> => {
		await axios.put(`${apiBase}/api/careers/applications/${encodeURIComponent(id)}/annotation`, annotation, {
			withCredentials: true
		});
	},

	/** Suppression DÉFINITIVE : ligne D1 + PDF R2 + annotation locale. */
	remove: async (id: string): Promise<void> => {
		await axios.delete(`${apiBase}/api/careers/applications/${encodeURIComponent(id)}`, {
			withCredentials: true,
			headers: { "X-Confirm-Destructive": "I understand" }
		});
	}
};
